import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Paper, Typography, Chip, Stack, LinearProgress } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import PageHeader from '../../components/common/PageHeader';
import ModuleControlPanel from '../../components/common/ModuleControlPanel';
import StatCard from '../../components/common/StatCard';
import { loadAiIntelligence } from './aiSlice';
import { severityChipSx, severityLabel, formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const RISK_COLOR = { critical: tokens.severity.critical, high: tokens.severity.high, medium: tokens.severity.medium, low: tokens.severity.low };

function scoreColor(score) {
  if (score >= 80) return tokens.severity.critical;
  if (score >= 60) return tokens.severity.high;
  return tokens.severity.medium;
}

export default function AiIntelligencePage() {
  const dispatch = useDispatch();
  const { alerts, timeline, predictions, behavioral } = useSelector((s) => s.ai);

  useEffect(() => { dispatch(loadAiIntelligence()); }, [dispatch]);

  const avgConfidence = predictions.length ? Math.round(predictions.reduce((s, p) => s + p.confidence, 0) / predictions.length) : 0;

  return (
    <Box>
      <PageHeader
        title="AI-Powered Security Intelligence"
        subtitle="Anomaly detection, threat prediction, and smart alert prioritization across the platform"
        action={<ModuleControlPanel basePath="/ai" moduleLabel="AI Intelligence" />}
      />

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}>
          <StatCard label="Prioritized Alerts" value={alerts.length} icon={AutoAwesomeRoundedIcon} accent={tokens.brand.main} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Active Predictions" value={predictions.length} accent={tokens.severity.info} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Avg. Prediction Confidence" value={`${avgConfidence}%`} accent={tokens.severity.medium} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>24H Anomaly Score</Typography>
            <Box sx={{ height: 180, mt: 1 }}>
              <ResponsiveContainer>
                <AreaChart data={timeline} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="anomalyGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={tokens.brand.main} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={tokens.brand.main} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={tokens.border} vertical={false} />
                  <XAxis dataKey="hour" tick={{ fill: tokens.text.muted, fontSize: 10 }} axisLine={{ stroke: tokens.border }} tickLine={false} interval={2} />
                  <YAxis tick={{ fill: tokens.text.muted, fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                  <Tooltip contentStyle={{ backgroundColor: tokens.bg.elevated, border: `1px solid ${tokens.border}`, borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="anomalyScore" name="Anomaly Score" stroke={tokens.brand.main} fill="url(#anomalyGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.4, mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>AI-Prioritized Alerts</Typography>
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {alerts.map((a) => (
                <Box key={a.id} sx={{ p: 1.4, borderRadius: 1.5, border: `1px solid ${tokens.border}`, backgroundColor: tokens.bg.elevated }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.6 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: tokens.text.primary, fontWeight: 500 }}>{a.title}</Typography>
                    <Chip label={severityLabel(a.severity)} size="small" sx={{ ...severityChipSx(a.severity), flexShrink: 0 }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                    <Typography variant="caption" sx={{ color: tokens.text.muted, minWidth: 68 }}>AI score {a.aiScore}</Typography>
                    <LinearProgress
                      variant="determinate" value={a.aiScore}
                      sx={{ flex: 1, height: 5, borderRadius: 4, backgroundColor: tokens.bg.overlay, '& .MuiLinearProgress-bar': { backgroundColor: scoreColor(a.aiScore), borderRadius: 4 } }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', color: tokens.brand.main }}>→ {a.suggestedAction}</Typography>
                  <Typography variant="caption" sx={{ color: tokens.text.muted }}>{formatRelativeTime(a.timestamp)}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.4, mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Threat Predictions</Typography>
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {predictions.map((p) => (
                <Box key={p.id} sx={{ p: 1.4, borderRadius: 1.5, border: `1px solid ${tokens.border}`, backgroundColor: tokens.bg.elevated }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: tokens.text.primary, fontWeight: 500 }}>{p.title}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: scoreColor(p.confidence), fontFamily: '"JetBrains Mono", monospace', flexShrink: 0, ml: 1 }}>
                      {p.confidence}%
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.74rem', color: tokens.text.secondary }}>{p.basis}</Typography>
                  <Typography variant="caption" sx={{ color: tokens.text.muted }}>Window: {p.window}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Behavioral Analytics</Typography>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {behavioral.map((b, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.6, borderBottom: i < behavioral.length - 1 ? `1px solid ${tokens.border}` : 'none' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: RISK_COLOR[b.risk], mt: 0.5, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.78rem', color: tokens.text.primary, fontFamily: '"JetBrains Mono", monospace' }}>{b.entity}</Typography>
                    <Typography sx={{ fontSize: '0.76rem', color: tokens.text.secondary }}>{b.insight}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
